import { Component } from "@/types";
import type { LineChartProperties } from "@/types/chart-properties";
import { ChartPropertyChange, commonChartHandler } from "./CommonChartHandler";
import { ChartDataPoint } from "../ChartDataManager";

/**
 * Line chart specific command handler
 */
export class LineChartHandler {
  /**
   * Parse line chart specific commands
   */

  /**
   * Phase 1.1: Line thickness preset values
   * Maps named thickness keywords to numeric strokeWidth values
   */
  private readonly thicknessPresets: Record<string, number> = {
    'hairline': 0.5,
    'thin': 1,
    'light': 1.5,
    'normal': 2,
    'default': 2,
    'medium': 2.5,
    'thick': 3,
    'heavy': 4,
    'bold': 4,
    'extra-thick': 5,
    'ultra-thick': 6,
  };

  /**
   * Phase 1.2: Line style preset values
   * Maps style names to strokeDasharray values
   */
  private readonly lineStylePresets: Record<string, string> = {
    'solid': '',
    'continuous': '',
    'dashed': '5 5',
    'dotted': '2 2',
    'dot': '2 2',
    'long-dash': '10 5',
    'short-dash': '3 3',
    'dash-dot': '5 2 2 2',
    'dash-dot-dot': '5 2 2 2 2 2',
    'sparse': '15 10',
    'dense': '2 1',
    'morse': '10 2 2 2 10 2',
  };

  /**
   * Phase 1.3: Marker shape mapping
   * Maps shape names to Recharts-compatible values
   */
  private readonly markerShapes: Record<string, string> = {
    'circle': 'circle',
    'round': 'circle',
    'dot': 'circle',
    'square': 'square',
    'rect': 'square',
    'rectangle': 'square',
    'diamond': 'diamond',
    'rhombus': 'diamond',
    'triangle': 'triangle',
    'tri': 'triangle',
    'star': 'star',
    'cross': 'cross',
    'plus': 'cross',
    'x': 'cross',
    'wye': 'wye',
  };

  /**
   * Phase 1.3: Named marker size presets
   */
  private readonly markerSizePresets: Record<string, number> = {
    'tiny': 2,
    'small': 4,
    'medium': 6,
    'large': 8,
    'big': 8,
    'huge': 12,
    'extra-large': 10,
  };

  /**
   * Phase 1.4: Curve type mapping
   * Maps curve names to Recharts curve types
   * @see https://recharts.org/en-US/api/Line#type
   */
  private readonly curveTypes: Record<string, string> = {
    'linear': 'linear',
    'straight': 'linear',
    'angular': 'linear',
    'monotone': 'monotone',
    'monotonex': 'monotoneX',
    'monotoney': 'monotoneY',
    'smooth': 'monotone',
    'natural': 'natural',
    'basis': 'basis',
    'basisopen': 'basisOpen',
    'basisclosed': 'basisClosed',
    'bundle': 'bundle',
    'cardinal': 'cardinal',
    'cardinalopen': 'cardinalOpen',
    'cardinalclosed': 'cardinalClosed',
    'catmullrom': 'catmullRom',
    'catmull-rom': 'catmullRom',
    'catmullromopen': 'catmullRomOpen',
    'catmullromclosed': 'catmullRomClosed',
    'step': 'step',
    'stepbefore': 'stepBefore',
    'step-before': 'stepBefore',
    'stepafter': 'stepAfter',
    'step-after': 'stepAfter',
    'stairs': 'step',
  };

  /**
   * Phase 1.4: Named tension presets for cardinal curves
   */
  private readonly tensionPresets: Record<string, number> = {
    'tight': 0.9,
    'firm': 0.7,
    'normal': 0.5,
    'loose': 0.3,
    'relaxed': 0.2,
    'very-loose': 0.1,
  };

  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    // First try common commands
    const commonChanges = commonChartHandler.parseCommand(command, component);

    // Then apply line-specific commands
    const lineChanges = this.parseLineSpecificCommand(command, component);

    // Merge changes
    if (commonChanges || lineChanges) {
      return {
        ...commonChanges,
        properties: {
          ...commonChanges?.properties,
          ...lineChanges?.properties,
        },
        style: {
          ...commonChanges?.style,
          ...lineChanges?.style,
        },
        data: commonChanges?.data || lineChanges?.data,
        type: commonChanges?.type || lineChanges?.type,
      };
    }

    return null;
  }

  private parseLineSpecificCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Line thickness/width
    if (this.isLineThicknessCommand(lowerCommand)) {
      const thicknessChanges = this.parseLineThicknessCommand(command, component);
      if (thicknessChanges) {
        changes.properties = { ...changes.properties, ...thicknessChanges };
      }
    }

    // Line style (dashed, dotted, etc.)
    if (this.isLineStyleCommand(lowerCommand)) {
      const styleChanges = this.parseLineStyleCommand(command, component);
      if (styleChanges) {
        changes.properties = { ...changes.properties, ...styleChanges };
      }
    }

    // Data point markers
    if (this.isMarkerCommand(lowerCommand)) {
      const markerChanges = this.parseMarkerCommand(command, component);
      if (markerChanges) {
        changes.properties = { ...changes.properties, ...markerChanges };
      }
    }

    // Line smoothing/interpolation
    if (this.isSmoothingCommand(lowerCommand)) {
      const smoothingChanges = this.parseSmoothingCommand(command);
      if (smoothingChanges) {
        changes.properties = { ...changes.properties, ...smoothingChanges };
      }
    }

    // Area fill (convert to area chart hybrid)
    if (this.isAreaCommand(lowerCommand)) {
      const areaChanges = this.parseAreaCommand(command);
      if (areaChanges) {
        changes.properties = { ...changes.properties, ...areaChanges };
      }
    }

    // Multiple lines
    if (this.isMultiLineCommand(lowerCommand)) {
      const multiLineChanges = this.parseMultiLineCommand(command, component);
      if (multiLineChanges) {
        changes.properties = { ...changes.properties, ...multiLineChanges.properties };
        if (multiLineChanges.data) {
          changes.data = multiLineChanges.data;
        }
      }
    }

    // Trend lines
    if (this.isTrendLineCommand(lowerCommand)) {
      const trendChanges = this.parseTrendLineCommand(command, component);
      if (trendChanges) {
        changes.properties = { ...changes.properties, ...trendChanges.properties };
        if (trendChanges.data) {
          changes.data = trendChanges.data;
        }
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isLineThicknessCommand(command: string): boolean {
    return /thick|thin|width|stroke|bold|weight|hairline|heavy|light|medium|double|halve|increase.*(?:thickness|width)|decrease.*(?:thickness|width)|\d+\s*(?:px)?\s+(?:line|stroke)/.test(command);
  }

  private parseLineThicknessCommand(command: string, component: Component): Partial<LineChartProperties> | null {
    const changes: Partial<LineChartProperties> = {};
    const currentWidth = typeof component.properties?.strokeWidth === 'number'
      ? component.properties.strokeWidth
      : 2;

    console.log(`[Phase 1.1] Parsing thickness command: "${command}", current width: ${currentWidth}`);

    // Pattern 1: Double/halve (must come first to avoid matching "thick" in "thickness")
    const doubleHalveMatch = command.match(/\b(double|halve)\b.*(?:thickness|width|stroke|line)/i);
    if (doubleHalveMatch) {
      const multiplier = doubleHalveMatch[1].toLowerCase() === 'double' ? 2 : 0.5;
      changes.strokeWidth = Math.max(0.5, Math.min(10, currentWidth * multiplier));
      console.log(`[Phase 1.1] ${doubleHalveMatch[1]} thickness: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 2: Increase/decrease by X (must come before numeric patterns)
    const adjustByMatch = command.match(/\b(increase|decrease)\b.*\bby\s+(\d+(?:\.\d+)?)/i);
    if (adjustByMatch) {
      const amount = parseFloat(adjustByMatch[2]);
      const isIncrease = adjustByMatch[1].toLowerCase() === 'increase';
      changes.strokeWidth = Math.max(0.5, Math.min(10, isIncrease ? currentWidth + amount : currentWidth - amount));
      console.log(`[Phase 1.1] Adjust by ${amount}: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 3: Specific numeric width value (explicit width/thickness/stroke keywords)
    const widthMatch = command.match(/(?:width|thickness|stroke)[:\s]+(\d+(?:\.\d+)?)/i) ||
                       command.match(/(\d+(?:\.\d+)?)\s*(?:px)?\s+(?:thick|width|stroke|line)/i) ||
                       command.match(/(?:set\s+)?(?:line\s+)?(?:to\s+)(\d+(?:\.\d+)?)\s*(?:px)?$/i);
    if (widthMatch) {
      const width = parseFloat(widthMatch[1]);
      changes.strokeWidth = Math.max(0.5, Math.min(10, width));
      console.log(`[Phase 1.1] Numeric width: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 4: Percentage adjustment
    const percentMatch = command.match(/(?:make\s+)?(\d+)\s*%\s+(thicker|thinner)/i);
    if (percentMatch) {
      const percent = parseInt(percentMatch[1]) / 100;
      const isThicker = percentMatch[2].toLowerCase() === 'thicker';
      const multiplier = isThicker ? (1 + percent) : (1 - percent);
      changes.strokeWidth = Math.max(0.5, Math.min(10, currentWidth * multiplier));
      console.log(`[Phase 1.1] Percentage adjustment: ${percentMatch[1]}% ${percentMatch[2]} → ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 5: Named thickness presets
    const presetMatch = command.match(/(?:use\s+|set\s+|make\s+(?:it\s+)?)?(?:line\s+)?(hairline|thin|light|normal|default|medium|thick|heavy|bold|extra-thick|ultra-thick)(?:\s+(?:line|stroke|width))?/i);
    if (presetMatch) {
      const presetName = presetMatch[1].toLowerCase();
      if (this.thicknessPresets[presetName] !== undefined) {
        changes.strokeWidth = this.thicknessPresets[presetName];
        console.log(`[Phase 1.1] Preset thickness "${presetName}": ${changes.strokeWidth}`);
        return changes;
      }
    }

    // Pattern 6: Relative thickness (existing patterns enhanced)
    if (command.includes("much thicker") || command.includes("way thicker")) {
      changes.strokeWidth = Math.min(currentWidth + 2, 10);
    } else if (command.includes("thicker") || command.includes("bolder") || command.includes("heavier")) {
      changes.strokeWidth = Math.min(currentWidth + 1, 10);
    } else if (command.includes("much thinner") || command.includes("way thinner")) {
      changes.strokeWidth = Math.max(currentWidth - 2, 0.5);
    } else if (command.includes("thinner") || command.includes("lighter")) {
      changes.strokeWidth = Math.max(currentWidth - 1, 0.5);
    }

    if (Object.keys(changes).length > 0) {
      console.log(`[Phase 1.1] Relative adjustment: ${changes.strokeWidth}`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isLineStyleCommand(command: string): boolean {
    return /dash|dot|solid|style|continuous|sparse|dense|morse|pattern|opacity|translucent|transparent|faded|opaque/.test(command);
  }

  private parseLineStyleCommand(command: string, _component: Component): Partial<LineChartProperties> | null {
    const changes: Partial<LineChartProperties> = {};

    console.log(`[Phase 1.2] Parsing line style command: "${command}"`);

    // Pattern 1: Named style presets (order matters - longer patterns first)
    const styleMatch = command.match(/(?:use\s+|make\s+(?:it\s+)?|set\s+)?(?:line\s+)?(?:style\s+)?(?:to\s+)?(solid|continuous|dashed|dotted|dot|long-dash|short-dash|dash-dot-dot|dash-dot|sparse|dense|morse)(?:\s+(?:line|style))?/i);
    if (styleMatch) {
      const styleName = styleMatch[1].toLowerCase();
      if (this.lineStylePresets[styleName] !== undefined) {
        changes.strokeDasharray = this.lineStylePresets[styleName];
        console.log(`[Phase 1.2] Style preset "${styleName}": "${changes.strokeDasharray}"`);
        return changes;
      }
    }

    // Pattern 2: Custom dash pattern
    const customPatternMatch = command.match(/(?:set\s+)?(?:custom\s+)?dash\s+pattern\s+(?:to\s+)?(\d+(?:\s+\d+)+)/i) ||
                               command.match(/(?:stroke\s+)?(?:dash\s+)?pattern\s+(\d+(?:\s+\d+)+)/i);
    if (customPatternMatch) {
      const pattern = customPatternMatch[1].trim();
      changes.strokeDasharray = pattern;
      console.log(`[Phase 1.2] Custom dash pattern: "${pattern}"`);
      return changes;
    }

    // Pattern 3: Line opacity (percentage)
    const opacityPercentMatch = command.match(/(?:set\s+)?(?:line\s+)?opacity\s+(?:to\s+)?(\d+)\s*%/i);
    if (opacityPercentMatch) {
      const opacity = parseInt(opacityPercentMatch[1]) / 100;
      changes.strokeOpacity = Math.max(0, Math.min(1, opacity));
      console.log(`[Phase 1.2] Opacity (percent): ${changes.strokeOpacity}`);
      return changes;
    }

    // Pattern 4: Line opacity (decimal)
    const opacityDecimalMatch = command.match(/(?:set\s+)?(?:line\s+)?opacity\s+(?:to\s+)?(0?\.\d+|1(?:\.0)?)/i);
    if (opacityDecimalMatch) {
      const opacity = parseFloat(opacityDecimalMatch[1]);
      changes.strokeOpacity = Math.max(0, Math.min(1, opacity));
      console.log(`[Phase 1.2] Opacity (decimal): ${changes.strokeOpacity}`);
      return changes;
    }

    // Pattern 5: Named opacity levels
    const namedOpacityMatch = command.match(/(?:make\s+)?(?:line\s+)?(translucent|semi-transparent|faded|transparent|invisible|opaque|visible)/i);
    if (namedOpacityMatch) {
      const opacityName = namedOpacityMatch[1].toLowerCase();
      const opacityMap: Record<string, number> = {
        'invisible': 0,
        'transparent': 0.1,
        'translucent': 0.4,
        'faded': 0.5,
        'semi-transparent': 0.6,
        'visible': 1,
        'opaque': 1,
      };
      changes.strokeOpacity = opacityMap[opacityName] ?? 1;
      console.log(`[Phase 1.2] Named opacity "${opacityName}": ${changes.strokeOpacity}`);
      return changes;
    }

    // Fallback: simple keyword matching (for backward compatibility)
    if (command.includes("dashed") || command.includes("dash")) {
      changes.strokeDasharray = "5 5";
    } else if (command.includes("dotted") || command.includes("dots")) {
      changes.strokeDasharray = "2 2";
    } else if (command.includes("solid") || command.includes("continuous")) {
      changes.strokeDasharray = "";
    }

    if (Object.keys(changes).length > 0) {
      console.log(`[Phase 1.2] Fallback style: strokeDasharray="${changes.strokeDasharray}"`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isMarkerCommand(command: string): boolean {
    return /dot|point|marker|circle|square|diamond|triangle|star|cross|hollow|filled|outlined|stroke|border/.test(command);
  }

  private parseMarkerCommand(command: string, component: Component): Partial<LineChartProperties> | null {
    const changes: Partial<LineChartProperties> = {};
    const currentSize = typeof component.properties?.dotSize === 'number'
      ? component.properties.dotSize
      : 4;

    console.log(`[Phase 1.3] Parsing marker command: "${command}", current size: ${currentSize}`);

    // Pattern 1: Show/hide markers
    const showHideMatch = command.match(/(show|hide|display|remove|add|enable|disable)\s+(?:the\s+)?(?:data\s+)?(?:points?|dots?|markers?)/i);
    if (showHideMatch) {
      const action = showHideMatch[1].toLowerCase();
      changes.showDots = ['show', 'display', 'add', 'enable'].includes(action);
      console.log(`[Phase 1.3] Show/hide markers: ${changes.showDots}`);
    }

    // Pattern 2: Marker shape
    const shapeMatch = command.match(/(?:use\s+|set\s+)?(?:marker|dot|point)\s+(?:shape\s+)?(?:to\s+)?(circle|round|dot|square|rect|rectangle|diamond|rhombus|triangle|tri|star|cross|plus|x|wye)/i) ||
                       command.match(/(circle|square|diamond|triangle|star|cross)\s+(?:markers?|dots?|points?)/i);
    if (shapeMatch) {
      const shapeName = shapeMatch[1].toLowerCase();
      if (this.markerShapes[shapeName]) {
        changes.dotShape = this.markerShapes[shapeName];
        console.log(`[Phase 1.3] Marker shape: ${changes.dotShape}`);
      }
    }

    // Pattern 3: Numeric marker size
    const numericSizeMatch = command.match(/(?:marker|dot|point)\s+size\s+(?:to\s+)?(\d+)/i) ||
                             command.match(/(\d+)\s*(?:px)?\s+(?:markers?|dots?|points?)/i) ||
                             command.match(/(?:set\s+)?(?:marker|dot|point)\s+(?:size\s+)?(?:to\s+)?(\d+)/i);
    if (numericSizeMatch) {
      changes.dotSize = Math.max(1, Math.min(20, parseInt(numericSizeMatch[1])));
      console.log(`[Phase 1.3] Numeric marker size: ${changes.dotSize}`);
    }

    // Pattern 4: Named marker size
    const namedSizeMatch = command.match(/(?:make\s+)?(?:markers?|dots?|points?)\s+(tiny|small|medium|large|big|huge|extra-large)/i) ||
                           command.match(/(tiny|small|medium|large|big|huge)\s+(?:markers?|dots?|points?)/i);
    if (namedSizeMatch) {
      const sizeName = namedSizeMatch[1].toLowerCase();
      if (this.markerSizePresets[sizeName] !== undefined) {
        changes.dotSize = this.markerSizePresets[sizeName];
        console.log(`[Phase 1.3] Named marker size "${sizeName}": ${changes.dotSize}`);
      }
    }

    // Pattern 5: Relative marker size
    if (command.includes("bigger") || command.includes("larger")) {
      changes.dotSize = Math.min(currentSize + 2, 20);
      console.log(`[Phase 1.3] Bigger markers: ${changes.dotSize}`);
    } else if (command.includes("smaller") || command.includes("tinier")) {
      changes.dotSize = Math.max(currentSize - 2, 1);
      console.log(`[Phase 1.3] Smaller markers: ${changes.dotSize}`);
    }

    // Pattern 6: Marker color
    const colorMatch = command.match(/(?:set\s+)?(?:marker|dot|point)\s+colou?r\s+(?:to\s+)?(#[0-9A-Fa-f]{3,6}|\w+)/i) ||
                       command.match(/(#[0-9A-Fa-f]{3,6})\s+(?:markers?|dots?|points?)/i);
    if (colorMatch) {
      changes.dotColor = colorMatch[1];
      console.log(`[Phase 1.3] Marker color: ${changes.dotColor}`);
    }

    // Pattern 7: Hollow/filled markers
    const fillMatch = command.match(/(?:make\s+)?(?:markers?|dots?|points?)\s+(hollow|filled|outlined|solid|empty)/i);
    if (fillMatch) {
      const fillType = fillMatch[1].toLowerCase();
      changes.dotFill = ['filled', 'solid'].includes(fillType) ? 'currentColor' : 'none';
      console.log(`[Phase 1.3] Marker fill: ${changes.dotFill}`);
    }

    // Pattern 8: Marker stroke width
    const strokeMatch = command.match(/(?:marker|dot|point)\s+(?:stroke|border|outline)\s+(?:width\s+)?(?:to\s+)?(\d+)/i) ||
                        command.match(/(\d+)\s*(?:px)?\s+(?:marker|dot|point)\s+(?:stroke|border|outline)/i);
    if (strokeMatch) {
      changes.dotStrokeWidth = Math.max(0, Math.min(5, parseInt(strokeMatch[1])));
      console.log(`[Phase 1.3] Marker stroke width: ${changes.dotStrokeWidth}`);
    }

    // Pattern 9: Conditional marker visibility
    const conditionalMatch = command.match(/(?:show\s+)?(?:markers?|dots?)\s+(?:only\s+)?(?:on|at)\s+(first|last|min|max|peaks?|valleys?|extremes?)/i);
    if (conditionalMatch) {
      const condition = conditionalMatch[1].toLowerCase();
      changes.dotVisibility = condition.replace(/s$/, ''); // normalize plurals
      console.log(`[Phase 1.3] Conditional marker visibility: ${changes.dotVisibility}`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isSmoothingCommand(command: string): boolean {
    return /smooth|curve|interpolat|linear|step|straight|natural|basis|bundle|cardinal|catmull|monotone|tension|connect.*(?:gap|null)|skip.*(?:gap|null)|break.*(?:gap|null)/.test(command);
  }

  private parseSmoothingCommand(command: string): Partial<LineChartProperties> | null {
    const changes: Partial<LineChartProperties> = {};

    console.log(`[Phase 1.4] Parsing curve/smoothing command: "${command}"`);

    // Pattern 1: Named tension values (check before curve types to avoid "smooth" matching)
    const tensionNamedMatch = command.match(/\b(tight|firm|loose|relaxed|very-loose)\s+(?:curve|smoothing|tension)/i);
    if (tensionNamedMatch) {
      const tensionName = tensionNamedMatch[1].toLowerCase();
      if (this.tensionPresets[tensionName] !== undefined) {
        changes.lineType = 'cardinal';
        changes.curveTension = this.tensionPresets[tensionName];
        console.log(`[Phase 1.4] Named tension "${tensionName}": ${changes.curveTension}`);
        return changes;
      }
    }

    // Pattern 2: Step variants (longer patterns first)
    const stepVariantMatch = command.match(/\b(stepbefore|step-before|stepafter|step-after)\b/i);
    if (stepVariantMatch) {
      const variant = stepVariantMatch[1].toLowerCase();
      if (variant === 'stepbefore' || variant === 'step-before') {
        changes.lineType = 'stepBefore';
      } else {
        changes.lineType = 'stepAfter';
      }
      console.log(`[Phase 1.4] Step variant: ${changes.lineType}`);
      return changes;
    }

    // Pattern 3: Step with direction
    const stepDirectionMatch = command.match(/\bstep\s+(before|after|center|middle)\b/i);
    if (stepDirectionMatch) {
      const stepType = stepDirectionMatch[1].toLowerCase();
      if (stepType === 'before') {
        changes.lineType = 'stepBefore';
      } else if (stepType === 'after') {
        changes.lineType = 'stepAfter';
      } else {
        changes.lineType = 'step';
      }
      console.log(`[Phase 1.4] Step direction: ${changes.lineType}`);
      return changes;
    }

    // Pattern 4: Named curve types (order matters - longer patterns first)
    const curveTypeMatch = command.match(/(?:use\s+|set\s+)?(?:line\s+)?(?:type\s+|curve\s+|interpolation\s+)?(?:to\s+)?(linear|straight|angular|monotonex|monotoney|monotone|smooth|natural|basisopen|basisclosed|basis|bundle|cardinalopen|cardinalclosed|cardinal|catmullromopen|catmullromclosed|catmullrom|catmull-rom|step|stairs)/i);
    if (curveTypeMatch) {
      const typeName = curveTypeMatch[1].toLowerCase();
      if (this.curveTypes[typeName]) {
        changes.lineType = this.curveTypes[typeName];
        console.log(`[Phase 1.4] Curve type: ${changes.lineType}`);
        return changes;
      }
    }

    // Pattern 5: Numeric tension value
    const tensionNumericMatch = command.match(/(?:set\s+)?(?:curve\s+)?tension\s+(?:to\s+)?(\d+(?:\.\d+)?)/i);
    if (tensionNumericMatch) {
      const tension = parseFloat(tensionNumericMatch[1]);
      changes.lineType = 'cardinal';
      changes.curveTension = Math.max(0, Math.min(1, tension > 1 ? tension / 100 : tension));
      console.log(`[Phase 1.4] Numeric tension: ${changes.curveTension}`);
      return changes;
    }

    // Pattern 6: Connect nulls
    if (command.includes("connect") && (command.includes("gap") || command.includes("null") || command.includes("missing"))) {
      changes.connectNulls = true;
      console.log(`[Phase 1.4] Connect nulls: true`);
    } else if (command.includes("skip") && (command.includes("gap") || command.includes("null") || command.includes("missing"))) {
      changes.connectNulls = false;
      console.log(`[Phase 1.4] Connect nulls: false`);
    } else if (command.includes("break") && (command.includes("gap") || command.includes("null"))) {
      changes.connectNulls = false;
      console.log(`[Phase 1.4] Break at gaps: connectNulls = false`);
    }

    // Fallback: simple keyword matching (backward compatibility)
    if (changes.lineType === undefined && Object.keys(changes).length === 0) {
      if (command.includes("very smooth") || command.includes("extra smooth")) {
        changes.lineType = "basis";
      } else if (command.includes("smooth") || command.includes("curved")) {
        changes.lineType = "monotone";
      } else if (command.includes("linear") || command.includes("straight")) {
        changes.lineType = "linear";
      } else if (command.includes("natural")) {
        changes.lineType = "natural";
      }

      if (changes.lineType) {
        console.log(`[Phase 1.4] Fallback curve type: ${changes.lineType}`);
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isAreaCommand(command: string): boolean {
    return /area|fill|shade/.test(command) && /under|below|beneath/.test(command);
  }

  private parseAreaCommand(command: string): Partial<LineChartProperties> | null {
    const changes: Partial<LineChartProperties> = {};

    if (command.includes("fill") || command.includes("shade") || command.includes("area")) {
      changes.area = true;

        // Parse opacity
      const opacityMatch = command.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:opacity|transparent)/i);
      if (opacityMatch) {
        let opacity = parseFloat(opacityMatch[1]);
        if (opacity > 1) opacity = opacity / 100; // Convert percentage
        changes.areaOpacity = opacity;
      } else if (command.includes("light") || command.includes("subtle")) {
        changes.areaOpacity = 0.2;
      } else if (command.includes("dark") || command.includes("solid")) {
        changes.areaOpacity = 0.8;
      } else {
        changes.areaOpacity = 0.3; // Default
      }
    } else if (command.includes("no fill") || command.includes("remove area")) {
      changes.area = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isMultiLineCommand(command: string): boolean {
    return /add.*line|another line|second line|multiple|series|compare/.test(command);
  }

  private parseMultiLineCommand(command: string, component: Component): ChartPropertyChange | null {
    const changes: ChartPropertyChange = {};
    const lineProps: Partial<LineChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];
    const rawLineKeys = component.properties?.lineKeys;
    const lineKeys = Array.isArray(rawLineKeys)
      ? rawLineKeys.filter((k): k is string => typeof k === 'string')
      : ["value"];

    // Add another line/series
    if (command.includes("add") && command.includes("line")) {
      // Parse line name
      const nameMatch = command.match(/line\s+(?:for|called|named)\s+["']([^"']+)["']/i) ||
                       command.match(/["']([^"']+)["']\s+line/i);
      const lineName = nameMatch ? nameMatch[1] : `Series ${lineKeys.length + 1}`;
      const lineKey = lineName.toLowerCase().replace(/\s+/g, "_");

      // Add to lineKeys
      if (!lineKeys.includes(lineKey)) {
        lineProps.lineKeys = [...lineKeys, lineKey];

        // Add sample data for the new line
        if (currentData.length > 0) {
          changes.data = currentData.map(point => ({
            ...point,
            [lineKey]: Math.floor(Math.random() * 100) + 20,
          }));
        }
      }
    }

    // Comparison data
    if (command.includes("compare") || command.includes("versus") || command.includes("vs")) {
      const compareMatch = command.match(/compare\s+(?:with|to)\s+["']([^"']+)["']/i) ||
                          command.match(/["']([^"']+)["']\s+vs\s+["']([^"']+)["']/i);
      if (compareMatch) {
        const series1 = compareMatch[1] || "Series 1";
        const series2 = compareMatch[2] || "Series 2";

        lineProps.lineKeys = [
          series1.toLowerCase().replace(/\s+/g, "_"),
          series2.toLowerCase().replace(/\s+/g, "_"),
        ];
      }
    }

    if (Object.keys(lineProps).length > 0) {
      changes.properties = lineProps;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isTrendLineCommand(command: string): boolean {
    return /trend|average|regression|forecast/.test(command);
  }

  private parseTrendLineCommand(command: string, component: Component): ChartPropertyChange | null {
    const changes: ChartPropertyChange = {};
    const lineProps: Partial<LineChartProperties> = {};
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];

    if (currentData.length === 0) return null;

    // Add moving average
    if (command.includes("moving average") || command.includes("smooth trend")) {
      const periodMatch = command.match(/(\d+)[\s-]?(?:period|point|day)/i);
      const period = periodMatch ? parseInt(periodMatch[1]) : 3;

      // Calculate moving average
      const movingAvg = this.calculateMovingAverage(currentData, "value", period);
      changes.data = currentData.map((point, index) => ({
        ...point,
        movingAverage: movingAvg[index],
      }));

      // Add to lineKeys
      const rawLineKeys1 = component.properties?.lineKeys;
      const lineKeys1 = Array.isArray(rawLineKeys1)
        ? rawLineKeys1.filter((k): k is string => typeof k === 'string')
        : ["value"];
      if (!lineKeys1.includes("movingAverage")) {
        lineProps.lineKeys = [...lineKeys1, "movingAverage"];
      }
    }

    // Add linear trend line
    if (command.includes("trend line") || command.includes("regression")) {
      const trend = this.calculateLinearTrend(currentData, "value");
      changes.data = currentData.map((point, index) => ({
        ...point,
        trend: trend[index],
      }));

      // Add to lineKeys
      const rawLineKeys2 = component.properties?.lineKeys;
      const lineKeys2 = Array.isArray(rawLineKeys2)
        ? rawLineKeys2.filter((k): k is string => typeof k === 'string')
        : ["value"];
      if (!lineKeys2.includes("trend")) {
        lineProps.lineKeys = [...lineKeys2, "trend"];
      }
    }

    if (Object.keys(lineProps).length > 0) {
      changes.properties = lineProps;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private calculateMovingAverage(data: ChartDataPoint[], valueKey: string, period: number): number[] {
    const result: number[] = [];

    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push(data[i][valueKey] || 0);
      } else {
        let sum = 0;
        for (let j = 0; j < period; j++) {
          sum += data[i - j][valueKey] || 0;
        }
        result.push(sum / period);
      }
    }

    return result;
  }

  private calculateLinearTrend(data: ChartDataPoint[], valueKey: string): number[] {
    const n = data.length;
    if (n === 0) return [];

    // Calculate linear regression
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    for (let i = 0; i < n; i++) {
      const x = i;
      const y = data[i][valueKey] || 0;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Generate trend line points
    return data.map((_, index) => intercept + slope * index);
  }

  /**
   * Get line chart specific suggestions
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [
      ...commonChartHandler.getSuggestions(component),
    ];

    // Line thickness suggestions
    const strokeWidth = typeof component.properties?.strokeWidth === 'number'
      ? component.properties.strokeWidth
      : 2;
    if (strokeWidth <= 2) {
      suggestions.push("Make the line thicker", "Use bold line", "Set line width 4");
    } else {
      suggestions.push("Make the line thinner", "Use thin line");
    }

    // Marker suggestions
    if (component.properties?.showDots !== false) {
      suggestions.push("Hide data points", "Use square markers", "Make dots smaller");
    } else {
      suggestions.push("Show data points", "Add circle markers");
    }

    // Line style suggestions
    if (!component.properties?.strokeDasharray) {
      suggestions.push("Use dashed line", "Make it dotted", "Use dash-dot style");
    } else {
      suggestions.push("Use solid line");
    }

    // Curve type suggestions
    const lineType = typeof component.properties?.lineType === 'string' ? component.properties.lineType : 'monotone';
    if (lineType === 'monotone') {
      suggestions.push("Use straight lines", "Use step interpolation", "Use natural curve");
    } else if (lineType === 'linear') {
      suggestions.push("Smooth the curve", "Use cardinal with tight tension");
    } else {
      suggestions.push("Use linear interpolation");
    }

    // Area fill suggestion
    if (!component.properties?.area) {
      suggestions.push("Fill area under the line");
    }

    // Multi-series suggestions
    const rawLineKeysSugg = component.properties?.lineKeys;
    const lineKeys = Array.isArray(rawLineKeysSugg)
      ? rawLineKeysSugg.filter((k): k is string => typeof k === 'string')
      : ["value"];
    if (lineKeys.length === 1) {
      suggestions.push("Add another line for comparison", "Add moving average", "Add trend line");
    }

    // Opacity suggestion
    if (component.properties?.strokeOpacity === undefined) {
      suggestions.push("Set line opacity to 80%");
    }

    return suggestions;
  }
}

export const lineChartHandler = new LineChartHandler();
